import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  List, ListItemButton, ListItemText, Stack, TextField, Typography,
} from '@mui/material';
import StyleConstants from '../../../core/styles.constants';
import type { EditableFloorPlan, EditorFeature } from '../models/editor-feature';
import { countMatches, findFeatures, FIND_LIMIT } from '../utils/find';

interface Props {
  readonly floorPlan: EditableFloorPlan | undefined;
  /** Selects the match and brings it into view. */
  readonly onGoTo: (feature: EditorFeature) => void;
}

const KIND_LABEL_KEYS: Readonly<Record<EditorFeature['kind'], string>> = {
  desk: 'floorplan-editor-tool-desk',
  deskShape: 'floorplan-editor-tool-desk-shape',
  textLabel: 'floorplan-editor-tool-text',
  iconLabel: 'floorplan-editor-tool-icon',
  polygon: 'floorplan-editor-find-kind-area',
};

/**
 * Finding one feature among several hundred identical-looking ones.
 *
 * The two questions this answers are "where is workplace 3f2c…", which arrives from a
 * support ticket carrying an id that is drawn nowhere on the plan, and "which of these is
 * the one labelled Kitchen". Neither is answerable by looking at the canvas.
 *
 * Searching is instant rather than behind a button: on a few hundred features it costs
 * nothing, and a search that only runs on Enter loses the one advantage of typing — watching
 * the list narrow.
 */
const FindPanel = ({ floorPlan, onGoTo }: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const matches = useMemo(() => findFeatures(floorPlan, query), [floorPlan, query]);

  // Only worth counting once the list is full — the count exists to explain what was left
  // out, and searching the whole plan a second time to say "3 of 3" is wasted work.
  const total = useMemo(
    () => (matches.length >= FIND_LIMIT ? countMatches(floorPlan, query) : matches.length),
    [matches.length, floorPlan, query],
  );

  return (
    <Stack sx={{ p: 1, minHeight: 0 }} spacing={1}>
      <TextField
        // Focused on mount, which is when the panel opens — Ctrl+F has to put the cursor
        // where the typing goes, or it is two actions pretending to be one.
        autoFocus
        size="small"
        value={query}
        onChange={(event): void => setQuery(event.target.value)}
        label={String(t('floorplan-editor-find'))}
        placeholder={String(t('floorplan-editor-find-placeholder'))}
        autoComplete="off"
        fullWidth
      />

      {query.trim().length > 0 && (
        <Typography variant="caption" color={StyleConstants.GREY_COLOR} aria-live="polite">
          {total > matches.length
            ? t('floorplan-editor-find-results-capped', { shown: matches.length, total })
            : t('floorplan-editor-find-results', { count: matches.length })}
        </Typography>
      )}

      <List dense sx={{ overflowY: 'auto' }}>
        {matches.map((match) => (
          <ListItemButton
            key={match.key}
            onClick={(): void => onGoTo(match.feature)}
            sx={{ borderRadius: 1 }}
          >
            <ListItemText
              primaryTypographyProps={{ variant: 'body2', noWrap: true }}
              primary={match.value}
              secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
              // Names the property that matched as well as the kind: a desk that turns up
              // for a query typed as a room name looks like a bug until it says why.
              secondary={`${t(KIND_LABEL_KEYS[match.feature.kind])} · ${t(match.fieldKey)}`}
            />
          </ListItemButton>
        ))}
      </List>
    </Stack>
  );
};

export default FindPanel;
